"use client";

import { c as _c } from "react/compiler-runtime";
import { formatCents } from "@/lib/utils/formatMoney";
import { MetricsPageSkeleton } from "@/app/(v2)/_components/PageSkeletons";
import { BlocksUsageTable } from "@/app/(v2)/billing/_components/BlocksUsageTable";
import { RecentActivityFeed } from "@/app/(v2)/billing/_components/RecentActivityFeed";
import { TopErrorsTable } from "@/app/(v2)/billing/_components/TopErrorsTable";
import { WorkflowCostBreakdown } from "@/app/(v2)/billing/_components/WorkflowCostBreakdown";
import { PieCenter, PieChart, PieSlice } from "@/components/charts";
import { Button } from "@/components/ui/button";
import { IncentivePanel } from "@/components/incentive/IncentivePanel";
import { PageLayout } from "@/components/ui/page-layout";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCUCosts, useCUUsage, useCUUsageByWorkflow } from "@/hooks/useCU";
import { useMetricsSummary } from "@/hooks/useMetricsSummary";
import { useOrganizations } from "@/hooks/useOrganizations";
import { usePlanUsage, type PlanUsageMetrics } from "@/hooks/usePlanUsage";
import { useSubscription } from "@/hooks/useSubscription";
import { useListTiers } from "@/hooks/useTiers";
import { cn } from "@/lib/utils";
import { currentYearMonth } from "@/lib/utils/date";
import { formatNumber } from "@/lib/utils/formatNumber";
import { safeParseFloat } from "@/lib/utils/safeParseFloat";
import { useBlocksStore } from "@/stores/blocksStore";
import type { TierInfo } from "@/types/billing";
import { getTierDisplayName } from "@/types/billing";
import type { CUCostsData, CUDailyUsage } from "@/types/cu";
import type { SubscriptionData } from "@/types/subscription";
import { IconConnectors1, IconPointChart, IconSparklesSoft, IconUserGroup, IconWarningSign } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { IconComputerOutline18 } from "nucleo-ui-outline-18";
import { type ComponentProps, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { BillingClientSkeleton } from "./BillingSkeletons";
import { humanizeName } from "./cost-categories";
import { CostCardGrid } from "./CostCardGrid";
import { SubscriptionTab } from "./SubscriptionTab";
import { UpgradePlanModal } from "./UpgradePlanModal";
type BillingTab = "overview" | "incentives" | "operations" | "costs" | "blocks" | "reference";
type OverviewChartTab = "spend" | "throughput";
type TopView = "plan-usage" | "subscription";
type UsageBarDatum = {
  day: number;
  dateLabel: string;
  total: number;
  height: number;
  topHeight: number;
  middleHeight: number;
  bottomHeight: number;
  showLabel: boolean;
  breakdown: Array<{
    label: string;
    value: number;
  }>;
};
type ThroughputBarDatum = {
  day: number;
  dateLabel: string;
  total: number;
  success: number;
  failure: number;
  totalHeight: number;
  successHeight: number;
  failureHeight: number;
  showLabel: boolean;
};
const VALID_UPGRADE_TIERS = new Set(["starter", "pro", "biz"]);
const BILLING_TABS: Array<{
  id: BillingTab;
  label: string;
}> = [{
  id: "overview",
  label: "Overview"
}, {
  id: "incentives",
  label: "Incentives"
}, {
  id: "operations",
  label: "Operations"
}, {
  id: "costs",
  label: "Costs"
}, {
  id: "blocks",
  label: "Blocks"
}, {
  id: "reference",
  label: "Reference"
}];
const PLAN_DESCRIPTIONS: Record<string, string> = {
  free: "A lightweight starting point for testing workflows and core automation.",
  starter: "Our most popular plan for individuals getting started with workflow automation.",
  pro: "Built for teams shipping faster with larger monthly spend and more seats.",
  biz: "For high-volume production workflows that need more headroom and support.",
  enterprise: "Custom pricing and limits for teams that need dedicated support and scale."
};
function isBillingTab(value: string | null): value is BillingTab {
  return BILLING_TABS.some(tab => tab.id === value);
}
function formatMoney(value?: number | null) {
  if (value == null) return "\u2014";
  return formatCents(Math.round(value * 100));
}
function formatLocalIsoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
function formatUsageDate(dateString: string) {
  return new Date(`${dateString}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}
function renderProgressText(primary: string, secondary: string) {
  return <span className="tabular-nums">
      <span className="text-ds-text-secondary dark:text-zinc-100">{primary}</span>{" "}
      <span className="text-ds-text-disabled dark:text-ds-text-muted">{secondary}</span>
    </span>;
}
function formatProgressText(used: number, total: number, label: string) {
  return renderProgressText(`${formatNumber(used)} of ${formatNumber(total)}`, label);
}
function buildUsageBars(daily: CUDailyUsage[], currentDate: Date): UsageBarDatum[] {
  const usageByDay = new Map<number, CUDailyUsage>();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const lastDay = currentDate.getDate();
  for (const entry of daily) {
    const day = new Date(`${entry.date}T00:00:00`).getDate();
    usageByDay.set(day, entry);
  }
  const values = Array.from({
    length: lastDay
  }, (_, index) => safeParseFloat(usageByDay.get(index + 1)?.used));
  const maxValue = Math.max(...values, 1);
  return Array.from({
    length: lastDay
  }, (_, index) => {
    const day = index + 1;
    const entry = usageByDay.get(day);
    const value = values[index] ?? 0;
    const height = value > 0 ? Math.max(34, Math.round(value / maxValue * 132)) : 8;
    const topHeight = value > 0 ? Math.max(12, Math.round(height * 0.32)) : 8;
    const middleHeight = value > 0 ? Math.max(10, Math.round(height * 0.29)) : 0;
    const bottomHeight = Math.max(0, height - topHeight - middleHeight);
    const synthesizedDate = formatLocalIsoDate(year, month, day);
    const breakdown = (entry?.breakdown ?? []).map(item => ({
      label: humanizeName(item.nodeType),
      value: safeParseFloat(item.cu)
    })).filter(item => item.value > 0 && item.label).sort((a, b) => b.value - a.value);
    return {
      day,
      dateLabel: formatUsageDate(entry?.date ?? synthesizedDate),
      total: safeParseFloat(entry?.used),
      height,
      topHeight,
      middleHeight,
      bottomHeight,
      showLabel: day % 2 === 0,
      breakdown
    };
  });
}
function buildThroughputBars(chartData: Array<{
  bucketStart: string;
  total: number;
  success: number;
  failure: number;
}>, currentDate: Date): ThroughputBarDatum[] {
  const byDay = new Map<number, {
    total: number;
    success: number;
    failure: number;
    bucketStart: string;
  }>();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const lastDay = currentDate.getDate();
  for (const entry of chartData) {
    const day = new Date(`${entry.bucketStart.slice(0, 10)}T00:00:00`).getDate();
    byDay.set(day, entry);
  }
  const totals = Array.from({
    length: lastDay
  }, (_, index) => byDay.get(index + 1)?.total ?? 0);
  const maxTotal = Math.max(...totals, 1);
  return Array.from({
    length: lastDay
  }, (_, index) => {
    const day = index + 1;
    const entry = byDay.get(day);
    const total = entry?.total ?? 0;
    const success = entry?.success ?? 0;
    const failure = entry?.failure ?? 0;
    const totalHeight = total > 0 ? Math.max(34, Math.round(total / maxTotal * 132)) : 8;
    let failureHeight = 0;
    if (failure > 0 && totalHeight > 8) {
      failureHeight = Math.max(8, Math.round(totalHeight * (failure / Math.max(total, 1))));
      failureHeight = Math.min(failureHeight, Math.max(8, totalHeight - 12));
    }
    let successHeight = total > 0 ? totalHeight - failureHeight : 0;
    if (total > 0 && successHeight < 12) {
      const deficit = 12 - successHeight;
      successHeight = 12;
      failureHeight = Math.max(0, failureHeight - deficit);
    }
    const synthesizedDate = formatLocalIsoDate(year, month, day);
    return {
      day,
      dateLabel: formatUsageDate(entry?.bucketStart.slice(0, 10) ?? synthesizedDate),
      total,
      success,
      failure,
      totalHeight,
      successHeight,
      failureHeight,
      showLabel: true
    };
  });
}
function buildInsightCards(daily: CUDailyUsage[], currentDate: Date) {
  if (daily.length === 0) return [];
  let totalCU = 0;
  let activeDays = 0;
  let peakDay = {
    date: "",
    cu: 0
  };
  const actionTotals = new Map<string, number>();
  const actionLastSeen = new Map<string, string>();
  for (const day of daily) {
    const used = safeParseFloat(day.used);
    totalCU += used;
    if (used > 0) activeDays += 1;
    if (used > peakDay.cu) {
      peakDay = {
        date: day.date,
        cu: used
      };
    }
    for (const breakdown of day.breakdown) {
      const cu = safeParseFloat(breakdown.cu);
      actionTotals.set(breakdown.nodeType, (actionTotals.get(breakdown.nodeType) ?? 0) + cu);
      if (cu > 0) {
        actionLastSeen.set(breakdown.nodeType, day.date);
      }
    }
  }
  const averageCU = daily.length > 0 ? totalCU / daily.length : 0;
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const projectedCU = Math.round(averageCU * daysInMonth);
  let topActionName = "";
  let topActionValue = 0;
  for (const [name, value] of actionTotals.entries()) {
    if (value > topActionValue) {
      topActionName = name;
      topActionValue = value;
    }
  }
  const peakDateLabel = peakDay.date ? formatUsageDate(peakDay.date) : "\u2014";
  const topActionDate = actionLastSeen.get(topActionName);
  const topActionDateLabel = topActionDate ? formatUsageDate(topActionDate) : peakDateLabel;
  return [{
    label: "Total Used",
    value: `${formatNumber(Math.round(totalCU))} CU`,
    detail: `across ${daily.length} day${daily.length === 1 ? "" : "s"}`
  }, {
    label: "Daily Average",
    value: `${formatNumber(Math.round(averageCU))} CU`,
    detail: `${activeDays} active day${activeDays === 1 ? "" : "s"}`
  }, {
    label: "Peak Day",
    value: `${formatNumber(Math.round(peakDay.cu))} CU`,
    detail: peakDateLabel
  }, {
    label: "Top Action",
    value: topActionName ? humanizeName(topActionName) : "No action data",
    detail: topActionDateLabel
  }, {
    label: "Month-end projection",
    value: `${formatNumber(projectedCU)} CU`,
    detail: `Based on ${daily.length}-day avg`
  }];
}
function SectionCard(t0) {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("ds-card dark:border-ds-neutral-800 dark:bg-ds-neutral-900", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function BillingTabBar(t0) {
  const $ = _c(12);
  const {
    value,
    onChange,
    items,
    variant: t1,
    dataGuide
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  let t2;
  if ($[0] !== onChange) {
    t2 = val => onChange(val as T);
    $[0] = onChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== items) {
    t3 = items.map(_temp);
    $[2] = items;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== dataGuide || $[5] !== t3) {
    t4 = <TabsList data-guide={dataGuide}>{t3}</TabsList>;
    $[4] = dataGuide;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4 || $[9] !== value || $[10] !== variant) {
    t5 = <Tabs value={value} onValueChange={t2} variant={variant} size="md">{t4}</Tabs>;
    $[7] = t2;
    $[8] = t4;
    $[9] = value;
    $[10] = variant;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function _temp(item) {
  return <TabsTrigger key={item.id} value={item.id}>{item.label}</TabsTrigger>;
}
function DetailMetric(t0) {
  const $ = _c(31);
  const {
    title,
    subtitle,
    progressText,
    progress,
    progressLabel,
    unlimited,
    icon,
    className
  } = t0;
  const clampedProgress = Math.max(0, Math.min(progress, 100));
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-col gap-3 p-4 sm:p-5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== icon) {
    t2 = <div className="shadow-ds-white-pressed-ring border-ds-neutral-300 dark:border-ds-neutral-700 bg-ds-surface-card relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md border">{icon}</div>;
    $[2] = icon;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== title) {
    t3 = <p className="text-ds-text-primary truncate text-sm font-semibold dark:text-zinc-100">{title}</p>;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== subtitle) {
    t4 = <p className="text-ds-text-muted truncate text-xs">{subtitle}</p>;
    $[6] = subtitle;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className="min-w-0 flex-1">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t2 || $[12] !== t5) {
    t6 = <div className="flex items-center gap-3">{t2}{t5}</div>;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== clampedProgress || $[15] !== unlimited) {
    t7 = unlimited ? <div className="from-ds-brand-500/20 via-ds-brand-500/10 to-ds-brand-500/20 shadow-ds-progress-track relative h-2 w-full overflow-hidden rounded-full bg-gradient-to-r dark:from-blue-500/20 dark:via-blue-500/10 dark:to-blue-500/20" /> : <div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 shadow-ds-progress-track relative h-2 w-full overflow-hidden rounded-full"><div className="bg-ds-brand-500 shadow-ds-brand-progress-glow absolute left-0 top-0 h-full rounded-full" style={{
        width: `${clampedProgress}%`
      }} /></div>;
    $[14] = clampedProgress;
    $[15] = unlimited;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== progressText) {
    t8 = <span className="min-w-0 truncate">{progressText}</span>;
    $[17] = progressText;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = unlimited ? "\u221E" : progressLabel ?? `${Math.round(clampedProgress)}%`;
  let t10;
  if ($[19] !== t9) {
    t10 = <span className="shrink-0 tabular-nums">{t9}</span>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t8) {
    t11 = <div className="text-ds-text-secondary flex items-center justify-between gap-2 text-xs font-medium">{t8}{t10}</div>;
    $[21] = t10;
    $[22] = t8;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t7) {
    t12 = <div className="flex flex-col gap-2">{t7}{t11}</div>;
    $[24] = t11;
    $[25] = t7;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t1 || $[28] !== t12 || $[29] !== t6) {
    t13 = <div className={t1}>{t6}{t12}</div>;
    $[27] = t1;
    $[28] = t12;
    $[29] = t6;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
}
function PlanUsageHero(t0) {
  const $ = _c(81);
  const {
    data,
    tierInfo,
    planMetrics,
    usedThisMonth,
    onUpgrade,
    onCancel
  } = t0;
  const {
    subscription,
    limits,
    usage
  } = data;
  let t1;
  if ($[0] !== subscription.tier) {
    t1 = getTierDisplayName(subscription.tier);
    $[0] = subscription.tier;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tierName = t1;
  const planDescription = PLAN_DESCRIPTIONS[subscription.tier] ?? PLAN_DESCRIPTIONS.free;
  const billedMonthly = tierInfo != null ? subscription.interval === "year" ? tierInfo.annualPriceUSDCents / 12 / 100 : tierInfo.priceUSDCents / 100 : null;
  let t2;
  if ($[2] !== planMetrics?.cu.ceiling || $[3] !== usage.cuBalance) {
    t2 = planMetrics?.cu.ceiling ?? safeParseFloat(usage.cuBalance);
    $[2] = planMetrics?.cu.ceiling;
    $[3] = usage.cuBalance;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const cuTotal = t2;
  const aiTotal = planMetrics?.ai.ceiling ?? limits.aiCredits;
  let t3;
  if ($[5] !== planMetrics?.ai.used || $[6] !== usage.aiCreditUsed) {
    t3 = planMetrics?.ai.used ?? safeParseFloat(usage.aiCreditUsed);
    $[5] = planMetrics?.ai.used;
    $[6] = usage.aiCreditUsed;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const aiUsed = t3;
  const hasWorkflowLimit = (planMetrics?.workflows.max ?? limits.maxWorkflows) > 0;
  const hasUserLimit = (planMetrics?.users.max ?? limits.maxUsers) > 0;
  const workflowTotal = hasWorkflowLimit ? planMetrics?.workflows.max ?? limits.maxWorkflows : Math.max(planMetrics?.workflows.active ?? usage.activeWorkflows, 1);
  const userTotal = hasUserLimit ? planMetrics?.users.max ?? limits.maxUsers : Math.max(planMetrics?.users.count ?? usage.memberCount, 1);
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const projectedCU = now.getDate() > 0 ? Math.round(usedThisMonth / now.getDate() * daysInMonth) : usedThisMonth;
  const showsCustomPricing = billedMonthly == null || subscription.tier === "enterprise";
  let t4;
  if ($[8] !== tierName) {
    t4 = <h2 className="text-ds-text-primary text-xl font-semibold tracking-tight">{tierName} Plan</h2>;
    $[8] = tierName;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = subscription.interval === "year" ? "Yearly" : "Monthly";
  let t6;
  if ($[10] !== t5) {
    t6 = <span className="border-ds-neutral-300 text-ds-text-secondary shadow-ds-soft dark:border-ds-neutral-700 dark:text-ds-text-disabled bg-ds-surface-card rounded-md border px-1.5 py-0.5 text-xs font-medium">{t5}</span>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <div className="flex items-center gap-2">{t4}{t6}</div>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== planDescription) {
    t8 = <p className="body-sm text-ds-text-subtle mt-2 max-w-[380px]">{planDescription}</p>;
    $[15] = planDescription;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <div className="min-w-0">{t7}{t8}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== billedMonthly || $[21] !== showsCustomPricing) {
    t10 = <div className="shrink-0 sm:pt-1">{showsCustomPricing ? <div className="text-right"><div className="text-ds-text-primary text-3xl font-semibold leading-none tracking-tight">Custom</div><div className="text-ds-text-muted pt-1 text-xs leading-tight">pricing</div></div> : <div className="flex items-start gap-2"><span className="text-ds-text-primary text-4xl font-semibold leading-none tracking-tight">{formatMoney(billedMonthly)}</span><div className="text-ds-text-muted pt-1 text-xs leading-tight"><div>USD /</div><div>month</div></div></div>}</div>;
    $[20] = billedMonthly;
    $[21] = showsCustomPricing;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t9) {
    t11 = <div className="flex flex-col gap-5 px-5 py-5 sm:px-6 sm:py-6"><div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">{t9}{t10}</div></div>;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== onCancel || $[27] !== subscription.tier) {
    t12 = subscription.tier !== "free" ? <button type="button" onClick={onCancel} className="text-ds-text-subtle hover:text-ds-text-primary dark:text-ds-text-disabled rounded-md px-2 py-1 text-xs font-medium transition-colors dark:hover:text-white">Cancel Subscription</button> : null;
    $[26] = onCancel;
    $[27] = subscription.tier;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = subscription.tier === "free" ? "Upgrade Plan" : "Change Plan";
  let t14;
  if ($[29] !== onUpgrade || $[30] !== t13) {
    t14 = <Button variant="black" onClick={onUpgrade} className="px-5 py-2 text-xs normal-case tracking-normal">{t13}</Button>;
    $[29] = onUpgrade;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t12 || $[33] !== t14) {
    t15 = <div className="border-ds-border-default mt-auto flex items-center justify-end gap-3 border-t px-5 py-4 sm:px-6">{t12}{t14}</div>;
    $[32] = t12;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t11 || $[36] !== t15) {
    t16 = <div className="xl:border-ds-neutral-200 dark:xl:border-ds-neutral-800 flex min-h-[170px] flex-col xl:border-r">{t11}{t15}</div>;
    $[35] = t11;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const t17 = limits.maxWorkflows === 0 ? "Unlimited workflow capacity" : `${Math.max(limits.maxWorkflows - usage.activeWorkflows, 0)} open slots`;
  let t18;
  if ($[38] !== limits.maxWorkflows || $[39] !== usage.activeWorkflows || $[40] !== workflowTotal) {
    t18 = limits.maxWorkflows === 0 ? renderProgressText(`${formatNumber(usage.activeWorkflows)}`, "active workflows") : formatProgressText(usage.activeWorkflows, workflowTotal, "active workflows");
    $[38] = limits.maxWorkflows;
    $[39] = usage.activeWorkflows;
    $[40] = workflowTotal;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const t19 = limits.maxWorkflows === 0 ? 0 : usage.activeWorkflows / workflowTotal * 100;
  const t20 = limits.maxWorkflows === 0;
  let t21;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <IconConnectors1 className="text-ds-text-muted h-4 w-4" />;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t17 || $[44] !== t18 || $[45] !== t19 || $[46] !== t20) {
    t22 = <DetailMetric title="Workflows" subtitle={t17} progressText={t18} progress={t19} unlimited={t20} icon={t21} className="border-ds-border-default border-b sm:border-r" />;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  const t23 = `Projected ${formatNumber(projectedCU)} CU this month`;
  let t24;
  if ($[48] !== cuTotal || $[49] !== usedThisMonth) {
    t24 = formatProgressText(usedThisMonth, cuTotal, "CU");
    $[48] = cuTotal;
    $[49] = usedThisMonth;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  const t25 = cuTotal > 0 ? usedThisMonth / cuTotal * 100 : 0;
  let t26;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <IconComputerOutline18 className="text-ds-text-muted h-4 w-4" />;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== t23 || $[53] !== t24 || $[54] !== t25) {
    t27 = <DetailMetric title="Compute units/month" subtitle={t23} progressText={t24} progress={t25} icon={t26} className="border-ds-border-default border-b" />;
    $[52] = t23;
    $[53] = t24;
    $[54] = t25;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== aiTotal || $[57] !== aiUsed) {
    t28 = formatProgressText(aiUsed, aiTotal, "AI credits");
    $[56] = aiTotal;
    $[57] = aiUsed;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  const t29 = aiTotal > 0 ? aiUsed / aiTotal * 100 : 0;
  let t30;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <IconSparklesSoft className="text-ds-text-muted h-4 w-4" />;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== t28 || $[61] !== t29) {
    t31 = <DetailMetric title="AI Credits" subtitle="Generation, suggestions, and chat" progressText={t28} progress={t29} icon={t30} className="border-ds-border-default border-b sm:border-b-0 sm:border-r" />;
    $[60] = t28;
    $[61] = t29;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  const t32 = limits.maxUsers === 0 ? "Unlimited seats" : `${Math.max(limits.maxUsers - usage.memberCount, 0)} open slots`;
  let t33;
  if ($[63] !== limits.maxUsers || $[64] !== usage.memberCount || $[65] !== userTotal) {
    t33 = limits.maxUsers === 0 ? renderProgressText(`${formatNumber(usage.memberCount)}`, "users") : formatProgressText(usage.memberCount, userTotal, "users");
    $[63] = limits.maxUsers;
    $[64] = usage.memberCount;
    $[65] = userTotal;
    $[66] = t33;
  } else {
    t33 = $[66];
  }
  const t34 = limits.maxUsers === 0 ? 0 : usage.memberCount / userTotal * 100;
  const t35 = limits.maxUsers === 0;
  let t36;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <IconUserGroup className="text-ds-text-muted h-4 w-4" />;
    $[67] = t36;
  } else {
    t36 = $[67];
  }
  let t37;
  if ($[68] !== t32 || $[69] !== t33 || $[70] !== t34 || $[71] !== t35) {
    t37 = <DetailMetric title="User Slots" subtitle={t32} progressText={t33} progress={t34} unlimited={t35} icon={t36} />;
    $[68] = t32;
    $[69] = t33;
    $[70] = t34;
    $[71] = t35;
    $[72] = t37;
  } else {
    t37 = $[72];
  }
  let t38;
  if ($[73] !== t22 || $[74] !== t27 || $[75] !== t31 || $[76] !== t37) {
    t38 = <div className="border-ds-border-default grid grid-cols-1 border-t sm:grid-cols-2 xl:border-t-0">{t22}{t27}{t31}{t37}</div>;
    $[73] = t22;
    $[74] = t27;
    $[75] = t31;
    $[76] = t37;
    $[77] = t38;
  } else {
    t38 = $[77];
  }
  let t39;
  if ($[78] !== t16 || $[79] !== t38) {
    t39 = <SectionCard className="overflow-hidden" data-guide="billing-plan-usage"><div className="grid gap-0 xl:grid-cols-[1.08fr_1.22fr]">{t16}{t38}</div></SectionCard>;
    $[78] = t16;
    $[79] = t38;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  return t39;
}
function OverviewUsageChart({
  daily,
  currentDate,
  isLoading
}: {
  daily: CUDailyUsage[];
  currentDate: Date;
  isLoading?: boolean;
}) {
  const bars = useMemo(() => buildUsageBars(daily, currentDate), [daily, currentDate]);
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  // tooltipLeft is computed in the mouse handler where chartRef is guaranteed
  // non-null, avoiding a ref access during render.
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0,
    tooltipLeft: 8
  });
  const chartRef = useRef<HTMLDivElement>(null);
  if (isLoading && daily.length === 0) {
    return <div className="py-3">
        <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 h-[198px] animate-pulse rounded-lg" />
      </div>;
  }
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!chartRef.current) return;
    const rect = chartRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const tooltipLeft = Math.min(Math.max(x - 90, 8), chartRef.current.clientWidth - 188);
    setMousePos({
      x,
      y,
      tooltipLeft
    });
  };
  const hoveredData = bars.find(b => b.day === hoveredBar);
  return <div className="pt-2">
      <div ref={chartRef} className="relative h-[198px] rounded-lg" onMouseMove={handleMouseMove}>
        <div className="pointer-events-none absolute inset-0 flex flex-col justify-between pb-8">
          {Array.from({
          length: 4
        }).map((_, index) => <div key={index} className="border-ds-neutral-100 dark:border-ds-neutral-800 border-t" />)}
        </div>
        <div className="absolute inset-x-0 bottom-0 top-0 flex items-end justify-between gap-1 px-1">
          {bars.map(bar => <div key={bar.day} className="group relative flex h-full flex-1 flex-col items-center justify-end gap-3" onMouseEnter={() => setHoveredBar(bar.day)} onMouseLeave={() => setHoveredBar(null)}>
              <div className={cn("flex w-full max-w-[28px] flex-col items-stretch transition-opacity duration-100", hoveredBar != null && hoveredBar !== bar.day && "opacity-40")} style={{
            height: `${bar.height}px`
          }}>
                <div className="rounded-t-ds-3 bg-ds-neutral-200 dark:bg-ds-neutral-700" style={{
              height: `${bar.topHeight}px`
            }} />
                <div className="bg-indigo-400 dark:bg-indigo-500" style={{
              height: `${bar.middleHeight}px`
            }} />
                <div className="bg-ds-brand-600 dark:bg-ds-brand-500 rounded-b-ds-3" style={{
              height: `${bar.bottomHeight}px`
            }} />
              </div>
              <span className="text-ds-text-secondary dark:text-ds-text-disabled text-xs font-normal tabular-nums">
                {bar.showLabel ? bar.day : "\u00A0"}
              </span>
            </div>)}
        </div>

        {hoveredData && hoveredData.total > 0 && <div className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 pointer-events-none absolute z-20 w-[180px] rounded-lg border p-3 text-xs" style={{
        left: `${mousePos.tooltipLeft}px`,
        top: `${Math.max(mousePos.y - 8, 4)}px`,
        transform: "translateY(-100%)"
      }}>
            <p className="text-ds-text-primary mb-1.5 font-semibold">{hoveredData.dateLabel}</p>
            <p className="text-ds-text-subtle mb-2 font-medium">{formatNumber(Math.round(hoveredData.total))} CU</p>
            {hoveredData.breakdown.length > 0 && <div className="border-ds-neutral-100 dark:border-ds-neutral-800 space-y-1 border-t pt-2">
                {hoveredData.breakdown.slice(0, 5).map(item => <div key={item.label} className="flex items-center justify-between gap-2">
                    <span className="text-ds-text-muted truncate">{item.label}</span>
                    <span className="text-ds-text-secondary shrink-0 tabular-nums">
                      {formatNumber(Math.round(item.value))}
                    </span>
                  </div>)}
              </div>}
          </div>}
      </div>
    </div>;
}
function RunsChart({
  chartData,
  currentDate
}: {
  chartData: Array<{
    bucketStart: string;
    total: number;
    success: number;
    failure: number;
  }>;
  currentDate: Date;
}) {
  const bars = useMemo(() => buildThroughputBars(chartData, currentDate), [chartData, currentDate]);
  if (bars.every(bar => bar.total === 0)) {
    return <div className="body-sm flex h-64 items-center justify-center">No run data available</div>;
  }
  return <TooltipProvider delayDuration={80}>
      <div className="pt-4">
        <div className="relative overflow-hidden rounded-lg">
          <div className="pointer-events-none absolute inset-x-0 bottom-9 top-0 flex flex-col justify-between">
            {Array.from({
            length: 4
          }).map((_, index) => <div key={index} className="border-ds-neutral-100 dark:border-ds-neutral-800 border-t" />)}
          </div>
          <div className="relative flex h-[230px] items-end gap-1.5 pb-9">
            {bars.map(bar_0 => <div key={bar_0.day} className="flex min-w-0 flex-1 flex-col items-center justify-end">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="group flex w-full max-w-[26px] items-end justify-center rounded-lg outline-none transition-transform duration-150 ease-out hover:-translate-y-px" aria-label={`${bar_0.dateLabel}: ${bar_0.total.toLocaleString()} runs`}>
                      {bar_0.total > 0 ? <div className="flex w-full flex-col overflow-hidden rounded-lg" style={{
                    height: `${bar_0.totalHeight}px`
                  }}>
                          {bar_0.failure > 0 ? <span className="w-full rounded-t-lg bg-red-300" style={{
                      height: `${bar_0.failureHeight}px`,
                      boxShadow: "0 0 8px rgba(239, 68, 68, 0.18)"
                    }} /> : null}
                          <span className={cn("bg-ds-brand-500 w-full", bar_0.failure === 0 ? "rounded-t-lg" : "")} style={{
                      height: `${bar_0.successHeight}px`,
                      boxShadow: "0 0 10px rgba(47, 67, 255, 0.24)"
                    }} />
                        </div> : <span className="bg-ds-neutral-200 dark:bg-ds-neutral-700 block h-2 w-full rounded-full" />}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="center" className="border-ds-border-default text-ds-text-primary dark:bg-ds-neutral-900/95 shadow-ds-elev-2 w-56 rounded-xl bg-white/95 px-3 py-3 backdrop-blur-md dark:text-zinc-100">
                    <p className="leading-label text-ds-text-muted text-xs font-medium">{bar_0.dateLabel}</p>
                    <p className="tracking-display-sm text-ds-text-primary mt-1 text-lg font-semibold leading-6 dark:text-zinc-100">
                      {bar_0.total.toLocaleString()} runs
                    </p>
                    <div className="mt-3 space-y-1.5">
                      <div className="leading-label flex items-center justify-between gap-3 text-xs">
                        <span className="text-ds-text-muted">Successful</span>
                        <span className="text-ds-text-primary font-medium tabular-nums dark:text-zinc-100">
                          {bar_0.success.toLocaleString()}
                        </span>
                      </div>
                      <div className="leading-label flex items-center justify-between gap-3 text-xs">
                        <span className="text-ds-text-muted">Failed</span>
                        <span className="text-ds-text-primary font-medium tabular-nums dark:text-zinc-100">
                          {bar_0.failure.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>)}
          </div>
          <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5">
            {bars.map(bar_1 => <div key={`throughput-label-${bar_1.day}`} className="flex min-w-0 flex-1 justify-center">
                <span className="text-2xs leading-label text-ds-text-muted font-medium tabular-nums">
                  {bar_1.showLabel ? bar_1.day : ""}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </TooltipProvider>;
}
function CostShareChart({
  organizationId,
  from,
  to
}: {
  organizationId?: string;
  from?: number;
  to?: number;
}) {
  const {
    data,
    isLoading
  } = useCUUsageByWorkflow(organizationId, from, to);
  const pieData = useMemo(() => {
    const workflows = data?.workflows ?? [];
    if (workflows.length === 0) return [];
    const palette = ["#2450FF", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#6366f1", "#84cc16"];
    const sorted = [...workflows].sort((a, b) => safeParseFloat(b.totalCu) - safeParseFloat(a.totalCu));
    const top = sorted.slice(0, 8);
    const rest = sorted.slice(8);
    const restTotal = rest.reduce((sum, workflow) => sum + safeParseFloat(workflow.totalCu), 0);
    const items = top.map((workflow_0, index) => ({
      label: workflow_0.workflowName,
      value: safeParseFloat(workflow_0.totalCu),
      color: palette[index % palette.length]
    }));
    if (restTotal > 0) {
      items.push({
        label: "Other",
        value: restTotal,
        color: "#94a3b8"
      });
    }
    return items;
  }, [data?.workflows]);
  if (isLoading) {
    return <SectionCard className="p-6">
        <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 h-48 animate-pulse rounded-lg" />
      </SectionCard>;
  }
  if (pieData.length === 0) return null;
  return <SectionCard className="p-6">
      <div className="mb-5">
        <h2 className="heading-lg">CU Distribution</h2>
        <p className="body-sm mt-1">How compute is distributed across workflows.</p>
      </div>
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start sm:justify-center">
        <PieChart data={pieData} size={220} innerRadius={65} padAngle={0.02} cornerRadius={4}>
          {pieData.map((_, index_0) => <PieSlice key={index_0} index={index_0} fill={pieData[index_0].color} />)}
          <PieCenter defaultLabel="Total CU" formatOptions={{
          notation: "compact",
          maximumFractionDigits: 1
        }} />
        </PieChart>
        <div className="flex flex-1 flex-col gap-2 pt-2">
          {pieData.map(item => <div key={item.label} className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{
            backgroundColor: item.color
          }} />
              <span className="body-sm min-w-0 flex-1 truncate">{item.label}</span>
              <span className="mono-xs tabular-nums">
                {item.value >= 1000 ? `${(item.value / 1000).toFixed(1)}k` : item.value.toFixed(0)}
              </span>
            </div>)}
        </div>
      </div>
    </SectionCard>;
}
function CostScheduleSection(t0) {
  const $ = _c(4);
  const {
    data,
    isLoading
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mb-5 flex items-center gap-3"><div className="shadow-ds-white-pressed-ring border-ds-neutral-300 dark:border-ds-neutral-700 bg-ds-surface-card relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md border"><IconPointChart className="text-ds-text-subtle h-5 w-5" /></div><div><h2 className="text-ds-text-primary text-base font-semibold">Cost Schedule</h2><p className="body-sm mt-1">Reference pricing for the compute actions contributing to this month's spend.</p></div></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== data || $[2] !== isLoading) {
    t2 = <SectionCard className="p-6">{t1}<CostCardGrid data={data} isLoading={isLoading} /></SectionCard>;
    $[1] = data;
    $[2] = isLoading;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export default function BillingClient() {
  const {
    selectedOrgId
  } = useOrganizations();
  const router = useRouter();
  const searchParams = useSearchParams();
  const rawTab = searchParams.get("tab");
  const activeTab: BillingTab = isBillingTab(rawTab) ? rawTab : "overview";
  const rawUpgradeParam = searchParams.get("upgrade");
  const upgradeTier = rawUpgradeParam && VALID_UPGRADE_TIERS.has(rawUpgradeParam) ? rawUpgradeParam : undefined;
  const [topView, setTopView] = useState<TopView>(() => upgradeTier ? "subscription" : "plan-usage");
  const [overviewChartTab, setOverviewChartTab] = useState<OverviewChartTab>("spend");
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planModalInitialTier, setPlanModalInitialTier] = useState<string | undefined>(undefined);
  const [currentDate, setCurrentDate] = useState(() => new Date());
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentDate(new Date());
    }, 60_000);
    return () => window.clearInterval(intervalId);
  }, []);
  const currentMonth = currentYearMonth(currentDate);
  const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getTime();
  const monthEnd = currentDate.getTime() + 5 * 60 * 1000;
  const usageQuery = useCUUsage(selectedOrgId ?? undefined, currentMonth);
  const subscriptionQuery = useSubscription(selectedOrgId ?? undefined);
  const planMetrics = usePlanUsage(selectedOrgId ?? undefined, {
    yearMonth: currentMonth
  });
  const costsQuery = useCUCosts();
  const {
    data: tiers
  } = useListTiers();
  const metricsSummaryQuery = useMetricsSummary({
    from: monthStart,
    to: monthEnd,
    granularity: "daily",
    topErrorsLimit: 10,
    recentActivityLimit: 50
  });
  const timeSeriesQuery = {
    ...metricsSummaryQuery,
    data: metricsSummaryQuery.data?.runsTimeSeries.data ?? [],
    error: metricsSummaryQuery.data?.runsTimeSeriesError ? new Error(metricsSummaryQuery.data.runsTimeSeriesError) : metricsSummaryQuery.error
  };
  const failureRateQuery = {
    ...metricsSummaryQuery,
    data: metricsSummaryQuery.data?.failureRate ?? null,
    error: metricsSummaryQuery.data?.failureRateError ? new Error(metricsSummaryQuery.data.failureRateError) : metricsSummaryQuery.error
  };
  const topErrorsQuery = {
    ...metricsSummaryQuery,
    data: metricsSummaryQuery.data?.topErrors ?? [],
    error: metricsSummaryQuery.data?.topErrorsError ? new Error(metricsSummaryQuery.data.topErrorsError) : metricsSummaryQuery.error
  };
  const recentActivityQuery = {
    ...metricsSummaryQuery,
    data: metricsSummaryQuery.data?.recentActivity ?? [],
    error: metricsSummaryQuery.data?.recentActivityError ? new Error(metricsSummaryQuery.data.recentActivityError) : metricsSummaryQuery.error
  };
  const loadBlocks = useBlocksStore(state => state.loadBlocks);
  const blocks = useBlocksStore(state_0 => state_0.blocks);
  const blocksLoading = useBlocksStore(state_1 => state_1.loading);
  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);
  useEffect(() => {
    if (!upgradeTier || !subscriptionQuery.data) return;
    // Open the UpgradePlanModal with the requested tier pre-selected. Replaces
    // the old PlanOverview `defaultModalOpen` auto-open behaviour that used
    // to handle this deep link.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: opening modal from URL param deep-link is a one-time effect, not a synchronous update loop
    setPlanModalInitialTier(upgradeTier);
    setPlanModalOpen(true);
    const params = new URLSearchParams(searchParams.toString());
    params.delete("upgrade");
    const next = params.toString();
    router.replace(next ? `/billing?${next}` : "/billing", {
      scroll: false
    });
  }, [upgradeTier, subscriptionQuery.data, router, searchParams]);
  const currentTierInfo = tiers?.find(tier => tier.name.toLowerCase() === subscriptionQuery.data?.subscription.tier);
  const chartData = timeSeriesQuery.data;
  const insightCards = useMemo(() => buildInsightCards(usageQuery.data?.daily ?? [], currentDate), [currentDate, usageQuery.data?.daily]);
  const errorMessages = [subscriptionQuery.error, usageQuery.error, timeSeriesQuery.error, failureRateQuery.error, topErrorsQuery.error, recentActivityQuery.error, costsQuery.error].map(error => error instanceof Error ? error.message : error).filter(Boolean);
  const isInitialLoad = subscriptionQuery.isLoading && !subscriptionQuery.data || usageQuery.isLoading && !usageQuery.data && metricsSummaryQuery.isLoading;
  const handleBillingTabChange = (nextTab: BillingTab) => {
    const params_0 = new URLSearchParams(searchParams.toString());
    if (nextTab === "overview") {
      params_0.delete("tab");
    } else {
      params_0.set("tab", nextTab);
    }
    const next_0 = params_0.toString();
    router.replace(next_0 ? `/billing?${next_0}` : "/billing", {
      scroll: false
    });
  };
  const openUpgradeModal = (initialTier?: string) => {
    setPlanModalInitialTier(initialTier);
    setPlanModalOpen(true);
  };
  if (isInitialLoad) {
    return <PageLayout backgroundClassName="bg-transparent" cardClassName="ds-shell min-h-[calc(100dvh-20px)] flex flex-col dark:border-ds-neutral-800 dark:bg-zinc-950 dark:shadow-none" contentPadding="px-5 py-5 sm:px-6 sm:py-6">
        <BillingClientSkeleton />
      </PageLayout>;
  }
  return <PageLayout backgroundClassName="bg-transparent" cardClassName="ds-shell min-h-[calc(100dvh-20px)] flex flex-col dark:border-ds-neutral-800 dark:bg-zinc-950 dark:shadow-none" contentPadding="px-5 py-5 sm:px-6 sm:py-6">
      <div className="font-geist relative pr-0 lg:pr-[42px]">
        <div className="mb-6">
          <h1 className="text-ds-text-primary text-3xl font-semibold tracking-tight">Plan &amp; Billing</h1>
          <p className="body-sm text-ds-text-subtle mt-1">
            Track compute spend, credit runway, and workflow reliability in one place.
          </p>
        </div>

        <div className="mb-6">
          <BillingTabBar<TopView> value={topView} onChange={setTopView} items={[{
          id: "plan-usage",
          label: "Plan usage"
        }, {
          id: "subscription",
          label: "Subscription"
        }]} />
        </div>

        {errorMessages.length > 0 ? <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100">
            <IconWarningSign className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Some billing data is unavailable.</p>
              <p className="mt-1 text-amber-800 dark:text-amber-200">{errorMessages[0]}</p>
            </div>
          </div> : null}

        {topView === "plan-usage" && subscriptionQuery.data ? <div className="space-y-6">
            <PlanUsageHero data={subscriptionQuery.data} tierInfo={currentTierInfo} planMetrics={planMetrics} usedThisMonth={safeParseFloat(usageQuery.data?.summary.used)} onUpgrade={() => openUpgradeModal()} onCancel={() => openUpgradeModal("free")} />

            <div>
              <div className="mb-4">
                <h2 className="heading-lg text-ds-text-primary">Metrics</h2>
                <p className="body-sm text-ds-text-subtle mt-1">
                  Track your workflow metrics, CU spend, throughput, etc.
                </p>
              </div>

              <div className="mb-4">
                <BillingTabBar<BillingTab> value={activeTab} onChange={handleBillingTabChange} items={BILLING_TABS} dataGuide="billing-tabs" />
              </div>

              {activeTab === "overview" ? <>
                  <SectionCard className="p-5 sm:p-6" data-guide={overviewChartTab === "throughput" ? "metrics-chart" : undefined}>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className="text-ds-text-primary text-base font-semibold">
                          {overviewChartTab === "spend" ? "Compute Unit Spend" : "Execution Throughput"}
                        </h3>
                        <p className="text-ds-text-muted mt-0.5 text-sm">
                          {overviewChartTab === "spend" ? "Monthly CU usage by trigger and action category." : "Success and failure volume across the current month."}
                        </p>
                      </div>
                      <BillingTabBar<OverviewChartTab> value={overviewChartTab} onChange={setOverviewChartTab} items={[{
                  id: "spend",
                  label: "Spend"
                }, {
                  id: "throughput",
                  label: "Throughput"
                }]} variant="ghost" />
                    </div>

                    {overviewChartTab === "spend" ? <OverviewUsageChart daily={usageQuery.data?.daily ?? []} currentDate={currentDate} isLoading={usageQuery.isLoading} /> : <div className="pt-4">
                        <RunsChart chartData={chartData} currentDate={currentDate} />
                      </div>}
                  </SectionCard>

                  {usageQuery.isLoading && insightCards.length === 0 ? <div className="mt-4">
                      <MetricsPageSkeleton />
                    </div> : <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5" data-guide="metrics-insights">
                      {insightCards.map(card => <SectionCard key={card.label} className="rounded-xl p-4 sm:p-5">
                          <p className="leading-label text-ds-text-muted text-sm font-medium">{card.label}</p>
                          <p className="tracking-display-sm text-ds-text-primary mt-4 text-xl font-semibold leading-7">
                            {card.value}
                          </p>
                          <p className="leading-label text-ds-text-muted mt-1 text-sm font-medium">{card.detail}</p>
                        </SectionCard>)}
                    </div>}
                </> : null}

              {activeTab === "incentives" ? <IncentivePanel variant="billing" /> : null}

              {activeTab === "operations" ? <div className="space-y-4">
                  <SectionCard className="p-5 sm:p-6" data-guide="metrics-chart">
                    <h3 className="heading-lg text-ds-text-primary">Execution Throughput</h3>
                    <p className="body-sm text-ds-text-subtle mt-1">
                      Success and failure volume across the current month.
                    </p>
                    <div className="pt-4">
                      <RunsChart chartData={chartData} currentDate={currentDate} />
                    </div>
                  </SectionCard>

                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <SectionCard className="p-5 sm:p-6" data-guide="metrics-errors">
                      <div className="mb-5">
                        <h3 className="heading-lg text-ds-text-primary">Top Errors</h3>
                        <p className="body-sm text-ds-text-subtle mt-1">
                          The most frequent recent failures so you can prioritize fixes.
                        </p>
                      </div>
                      <TopErrorsTable errors={topErrorsQuery.data} isLoading={topErrorsQuery.isLoading} recentActivities={recentActivityQuery.data} hideHeader />
                    </SectionCard>

                    <SectionCard className="p-5 sm:p-6" data-guide="metrics-activity">
                      <div className="mb-5 flex items-start justify-between gap-4">
                        <div>
                          <h3 className="heading-lg text-ds-text-primary">Recent Activity</h3>
                          <p className="body-sm text-ds-text-subtle mt-1">
                            Latest workflow runs and their current state.
                          </p>
                        </div>
                        <Link href="/workflows/history" className="text-ds-text-muted hover:text-ds-text-secondary inline-flex items-center gap-1 whitespace-nowrap text-xs dark:hover:text-zinc-300">
                          View all
                        </Link>
                      </div>
                      <RecentActivityFeed activities={recentActivityQuery.data} isLoading={recentActivityQuery.isLoading} hideHeader maxItems={10} />
                    </SectionCard>
                  </div>

                  <SectionCard className="p-5 sm:p-6">
                    <div className="mb-5">
                      <h3 className="heading-lg text-ds-text-primary">Reliability Snapshot</h3>
                      <p className="body-sm text-ds-text-subtle mt-1">
                        Execution health indicators for the current month.
                      </p>
                    </div>
                    {failureRateQuery.isLoading ? <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 h-48 animate-pulse rounded-lg" /> : <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
                        <PieChart data={[{
                  label: "Success",
                  value: (failureRateQuery.data?.total ?? 0) - (failureRateQuery.data?.failed ?? 0)
                }, {
                  label: "Failed",
                  value: failureRateQuery.data?.failed ?? 0
                }].filter(item => item.value > 0)} size={180} innerRadius={55} padAngle={0.03} cornerRadius={4}>
                          <PieSlice index={0} fill="#22c55e" />
                          <PieSlice index={1} fill="#ef4444" />
                          <PieCenter defaultLabel="Success Rate" suffix="%" formatOptions={{
                    maximumFractionDigits: 1
                  }}>
                            {({
                      isHovered,
                      data: hoveredData
                    }) => isHovered ? <div className="flex flex-col items-center">
                                  <span className="text-xl font-bold">{hoveredData.value.toLocaleString()}</span>
                                  <span className="text-ds-text-muted text-xs">{hoveredData.label}</span>
                                </div> : <div className="flex flex-col items-center">
                                  <span className="text-xl font-bold">
                                    {(100 - (failureRateQuery.data?.failureRate ?? 0)).toFixed(1)}%
                                  </span>
                                  <span className="text-ds-text-muted text-xs">Success Rate</span>
                                </div>}
                          </PieCenter>
                        </PieChart>
                        <div className="flex flex-1 flex-col gap-3 pt-2">
                          <div className="flex items-center gap-3">
                            <span className="h-3 w-3 rounded-full bg-green-500" />
                            <span className="body-sm-medium flex-1">Successful</span>
                            <span className="font-mono text-sm font-medium tabular-nums">
                              {((failureRateQuery.data?.total ?? 0) - (failureRateQuery.data?.failed ?? 0)).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="h-3 w-3 rounded-full bg-red-500" />
                            <span className="body-sm-medium flex-1">Failed</span>
                            <span className="font-mono text-sm font-medium tabular-nums">
                              {(failureRateQuery.data?.failed ?? 0).toLocaleString()}
                            </span>
                          </div>
                          <div className="border-ds-border-default mt-1 border-t pt-3">
                            <div className="flex items-center gap-3">
                              <span className="body-sm flex-1">Total runs</span>
                              <span className="font-mono text-sm font-medium tabular-nums">
                                {(failureRateQuery.data?.total ?? 0).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>}
                  </SectionCard>
                </div> : null}

              {activeTab === "costs" ? <div className="space-y-4">
                  <CostShareChart organizationId={selectedOrgId ?? undefined} from={monthStart} to={monthEnd} />
                  <SectionCard className="p-5 sm:p-6">
                    <WorkflowCostBreakdown organizationId={selectedOrgId ?? undefined} from={monthStart} to={monthEnd} />
                  </SectionCard>
                </div> : null}

              {activeTab === "blocks" ? <SectionCard className="p-5 sm:p-6">
                  <div className="mb-5">
                    <h3 className="heading-lg text-ds-text-primary">Block Adoption</h3>
                    <p className="body-sm text-ds-text-subtle mt-1">
                      Understand which reusable blocks are getting the most traction.
                    </p>
                  </div>
                  <BlocksUsageTable blocks={blocks} isLoading={blocksLoading} />
                </SectionCard> : null}

              {activeTab === "reference" ? <CostScheduleSection data={costsQuery.data} isLoading={costsQuery.isLoading} /> : null}
            </div>
          </div> : null}

        {topView === "subscription" ? <SubscriptionTab /> : null}

        {selectedOrgId && subscriptionQuery.data && planModalOpen ? <UpgradePlanModal open={planModalOpen} onOpenChange={setPlanModalOpen} currentTier={subscriptionQuery.data.subscription.tier} currentInterval={subscriptionQuery.data.subscription.interval ?? "month"} orgId={selectedOrgId} cancelAt={subscriptionQuery.data.subscription.cancelAt} pendingTier={subscriptionQuery.data.subscription.pendingTier} pendingTierAt={subscriptionQuery.data.subscription.pendingTierAt} pendingInterval={subscriptionQuery.data.subscription.pendingInterval} initialSelectedTier={planModalInitialTier} /> : null}
      </div>
    </PageLayout>;
}